import {isFeatureEnabled} from '@github-ui/feature-flags'
import {announce} from '@github-ui/aria-live'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports -- This existing form entry point uses TanStack Query, not Relay.
import {InlineMilestoneForm} from '@github-ui/repository-milestone/InlineMilestoneForm'
import {SPECIAL_VALUES} from '@github-ui/item-picker-base/constants/placeholders'
import {VALUES} from '@github-ui/item-picker-base/constants/values'
import {ItemPicker, type ExtendedItemProps} from '@github-ui/item-picker-base/ItemPicker'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {useDebounce} from '@github-ui/use-debounce'
import {useCallback, useDeferredValue, useMemo, useRef, useState} from 'react'
import {IconButton, SelectPanel, type SelectPanelItemProps as ItemProps} from '@primer/react'
import {ArrowLeftIcon, MilestoneIcon, PlusIcon} from '@primer/octicons-react'
import {MilestoneDescription} from './MilestoneDescription'
import type {ItemGroup} from '@github-ui/item-picker-base/shared'
import {LABELS} from '@github-ui/item-picker-base/constants/labels'
import {ERRORS} from '@github-ui/item-picker-base/constants/errors'
import {hasFuzzyMatch} from '@github-ui/fuzzy-score/has-fuzzy-match'
import type {BaseItemPickerProps, ItemPickerActivationProps} from '@github-ui/item-picker-base/base-item-picker-props'

import styles from './ItemPickerMilestone.module.css'

export type MilestonePickerMilestone = {
  id: string
  title: string
  closed: boolean
  closedAt: string | null | undefined
  dueOn: string | null | undefined
  progressPercentage: number
  url: string
}

export type MilestonePickerBaseProps = Omit<BaseItemPickerProps<MilestonePickerMilestone>, 'filterItems'> & {
  repo: string
  owner: string
  activeMilestone: MilestonePickerMilestone | null
  canCreateMilestone: boolean
  noMilestoneItem?: ExtendedItemProps<MilestonePickerMilestone>
  includeClosedMilestones?: boolean
  showMilestoneDescription?: boolean
  showNoMatchItem?: boolean
  repositoryId: string
  allowInlineCreation?: boolean
  viewerCanCreateMilestones?: boolean
  onMilestoneCreated?: (milestone: MilestonePickerMilestone) => void

  milestones: MilestonePickerMilestone[]
  setIsLoading: (isLoading: boolean) => void

  fetchMilestones: (options: {
    owner: string
    repo: string
    query: string
    count?: number
  }) => Promise<MilestonePickerMilestone[]>
  onCreate: CreateMilestoneMutationCallback
} & ItemPickerActivationProps

const openGroup: ItemGroup = {groupId: 'open', header: {title: 'Open', variant: 'filled'}}
const closedGroup: ItemGroup = {groupId: 'closed', header: {title: 'Closed', variant: 'filled'}}
const noMilestoneGroup: ItemGroup = {groupId: 'no-milestone'}

function compareMilestones(a: MilestonePickerMilestone, b: MilestonePickerMilestone): number {
  // Open milestones: sort by dueOn ascending (nearest deadline first)
  if (!a.closed && !b.closed) {
    if (a.dueOn && b.dueOn) return new Date(a.dueOn).getTime() - new Date(b.dueOn).getTime()
    if (a.dueOn) return -1
    if (b.dueOn) return 1
    return a.title.localeCompare(b.title)
  }
  // Closed milestones: sort by closedAt descending (most recently closed first)
  if (a.closed && b.closed) {
    if (a.closedAt && b.closedAt) return new Date(b.closedAt).getTime() - new Date(a.closedAt).getTime()
    if (a.closedAt) return -1
    if (b.closedAt) return 1
    return a.title.localeCompare(b.title)
  }
  // Open before closed
  return a.closed ? 1 : -1
}

export type MutationCallback<TVariables, TResponse> = (
  variables: TVariables,
  options: {onSuccess: (response: TResponse | null) => void; onError: () => void},
) => void
export type CreateMilestoneVariables = {repositoryId: string; title: string}
export type CreateMilestoneMutationCallback = MutationCallback<CreateMilestoneVariables, MilestonePickerMilestone>

export function MilestonePickerBase({
  repo,
  owner,
  milestones,
  repositoryId,
  activeMilestone,
  onSelectionChange,
  onItemActivation,
  insidePortal,
  anchorElement,
  nested,
  triggerOpen,
  initialFilter,
  isLoading,
  setIsLoading,
  noMilestoneItem,
  includeClosedMilestones = !noMilestoneItem,
  showMilestoneDescription,
  title,
  canCreateMilestone,
  fetchMilestones,
  onCreate,
  showNoMatchItem = false,
  anchorSide,
  readonly = false,
  allowInlineCreation = false,
  viewerCanCreateMilestones = false,
  onMilestoneCreated,
}: MilestonePickerBaseProps) {
  const inlineCreationEnabled = allowInlineCreation && isFeatureEnabled('issues_inline_milestone_creation')
  const canCreateInline = inlineCreationEnabled && !readonly && viewerCanCreateMilestones && !!repositoryId
  const canCreate = inlineCreationEnabled ? canCreateInline : canCreateMilestone
  const [isCreateFormOpen, setIsCreateFormOpen] = useState(false)
  const [isCreatePending, setIsCreatePending] = useState(false)
  const [createInitialTitle, setCreateInitialTitle] = useState('')
  const filterInputRef = useRef<HTMLInputElement>(null)
  const [filter, setFilter] = useState('')
  const deferredFilter = useDeferredValue(filter)
  const [isServerFetching, setIsServerFetching] = useState(false)
  const [searchedMilestones, setSearchedMilestones] = useState<MilestonePickerMilestone[] | undefined>()
  const [loadedMilestones, setLoadedMilestones] = useState<Array<MilestonePickerMilestone & {__isNew__?: boolean}>>([])
  const {addToast} = useToastContext()

  const items = useMemo(() => {
    if (searchedMilestones !== undefined && searchedMilestones.length > 0) {
      return [
        ...searchedMilestones,
        ...loadedMilestones.filter(
          milestone =>
            milestone.title.toLowerCase().includes(deferredFilter.toLowerCase()) &&
            !searchedMilestones.some(item => item.id === milestone.id),
        ),
      ]
    }

    const repositoryMilestones = milestones
      .filter(milestone => !loadedMilestones.some(item => item.id === milestone.id))
      .sort(compareMilestones)
      .concat(loadedMilestones)

    if (noMilestoneItem) repositoryMilestones.unshift(SPECIAL_VALUES.noMilestoneData as MilestonePickerMilestone)
    if (!deferredFilter) return repositoryMilestones
    return repositoryMilestones.filter(l => l.title.toLowerCase().indexOf(deferredFilter.toLowerCase()) >= 0)
  }, [milestones, deferredFilter, loadedMilestones, noMilestoneItem, searchedMilestones])

  const isNoMilestoneSelected = noMilestoneItem && noMilestoneItem.selected

  const visibleItems = useMemo(
    () => (includeClosedMilestones ? items : items.filter(m => !m.closed)),
    [includeClosedMilestones, items],
  )

  const searchMilestones = useCallback(
    async (query: string) => {
      setIsServerFetching(true)
      setFilter(query)

      try {
        const fetchedData = await fetchMilestones({query, owner, repo, count: 10})

        if (fetchedData !== undefined) {
          if (noMilestoneItem && hasFuzzyMatch(query, noMilestoneItem.source?.title ?? '')) {
            fetchedData.unshift(SPECIAL_VALUES.noMilestoneData as MilestonePickerMilestone)
          }

          setSearchedMilestones(fetchedData)
        }
      } catch {
        // Temporarily disabling this until we have a standard way of displaying fetch errors
        // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
        addToast({
          type: 'error',
          message: ERRORS.couldNotSearchMilestones,
        })
      } finally {
        setIsServerFetching(false)
      }
    },
    [addToast, fetchMilestones, noMilestoneItem, owner, repo],
  )

  const debounceFetchSearchData = useDebounce(
    (nextValue: string) => searchMilestones(nextValue),
    VALUES.pickerDebounceTime,
  )

  const filterItems = useCallback(
    async (value: string) => {
      const trimmedFilter = value.trim()
      if (filter === trimmedFilter) {
        return
      }

      if (trimmedFilter === '') {
        setSearchedMilestones(undefined)
        setFilter(trimmedFilter)
        return
      }

      debounceFetchSearchData(trimmedFilter)
    },
    [debounceFetchSearchData, filter],
  )

  const getItemKey = useCallback((milestone: MilestonePickerMilestone) => milestone.id, [])

  const openCreateForm = useCallback((initialTitle: string) => {
    setCreateInitialTitle(initialTitle)
    setIsCreateFormOpen(true)
  }, [])

  const closeCreateForm = useCallback(() => {
    if (isCreatePending) return
    setIsCreateFormOpen(false)
    requestAnimationFrame(() => filterInputRef.current?.focus())
  }, [isCreatePending])

  const focusForm = useCallback((element: HTMLDivElement | null) => {
    element?.querySelector<HTMLInputElement>('input')?.focus()
  }, [])

  const handleMilestoneCreated = useCallback(
    (milestone: MilestonePickerMilestone) => {
      onMilestoneCreated?.(milestone)
      setLoadedMilestones(current => [milestone, ...current.filter(item => item.id !== milestone.id)])
      announce(`Milestone "${milestone.title}" created.`)
      setIsCreateFormOpen(false)
      onSelectionChange([milestone])
      requestAnimationFrame(() => filterInputRef.current?.focus())
    },
    [onMilestoneCreated, onSelectionChange],
  )

  const isCreateAction = useCallback(
    (milestone: MilestonePickerMilestone) => inlineCreationEnabled && '__isCreateAction__' in milestone,
    [inlineCreationEnabled],
  )

  const createNewMilestone = useCallback(
    (
      _: ItemProps,
      milestone: MilestonePickerMilestone,
      event: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>,
    ) => {
      event.preventDefault()
      event.stopPropagation()

      if (!repositoryId) return
      setIsLoading(true)

      onCreate(
        {
          repositoryId,
          title: milestone.title,
        },
        {
          onError: () => {
            // Temporarily disabling this until we have a standard way of displaying fetch errors
            // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
            addToast({
              type: 'error',
              message: ERRORS.couldNotCreateMilestone,
            })
            setIsLoading(false)
          },
          onSuccess: newMilestone => {
            if (newMilestone) {
              setLoadedMilestones([{...newMilestone, __isNew__: true}, ...loadedMilestones])
            }
            setIsLoading(false)
          },
        },
      )
    },
    [addToast, loadedMilestones, onCreate, repositoryId, setIsLoading],
  )

  const convertToItemProps = useCallback(
    (milestone: MilestonePickerMilestone): ExtendedItemProps<MilestonePickerMilestone> => {
      if (noMilestoneItem && milestone.id === SPECIAL_VALUES.noMilestoneData.id) {
        return {
          ...noMilestoneItem,
          groupId: includeClosedMilestones ? noMilestoneGroup.groupId : noMilestoneItem.groupId,
        }
      }

      if (milestone.id === filter) {
        return {
          id: milestone.id,
          text: `Create new milestone: "${milestone.title}"`,
          source: milestone,
          trailingVisual: PlusIcon,
          className: styles.MilestoneFilterItem,
          onAction: (
            item: ItemProps,
            event: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>,
          ) => {
            if (canCreateInline) {
              event.preventDefault()
              event.stopPropagation()
              openCreateForm(milestone.title)
            } else {
              createNewMilestone(item, milestone, event)
            }
          },
        }
      }

      const {closed, closedAt, progressPercentage, dueOn} = milestone

      return {
        id: milestone.id,
        text: milestone.title,
        description: showMilestoneDescription
          ? ((
              <MilestoneDescription
                closed={closed}
                closedAt={closedAt}
                progressPercentage={progressPercentage}
                dueOn={dueOn}
                showProgressPercentage={false}
              />
            ) as unknown as string)
          : undefined,
        descriptionVariant: 'block',
        className: styles.MilestoneItem,
        source: milestone,
        groupId: milestone.closed ? closedGroup.groupId : openGroup.groupId,
        leadingVisual: () => <MilestoneIcon size={16} />,
      }
    },
    [
      canCreateInline,
      createNewMilestone,
      filter,
      includeClosedMilestones,
      noMilestoneItem,
      openCreateForm,
      showMilestoneDescription,
    ],
  )

  // Only show groups for which the filter returns at least one item
  const groups: ItemGroup[] = useMemo(() => {
    const itemGroups = []
    if (visibleItems.some(i => i.id === SPECIAL_VALUES.noMilestoneData.id)) {
      itemGroups.push(noMilestoneGroup)
    }
    if (visibleItems.some(i => !i.closed && i.id !== SPECIAL_VALUES.noMilestoneData.id)) {
      itemGroups.push(openGroup)
    }
    if (visibleItems.some(i => i.closed)) {
      itemGroups.push(closedGroup)
    }
    return itemGroups
  }, [visibleItems])

  if (noMilestoneItem && isNoMilestoneSelected) {
    // eslint-disable-next-line react-hooks/immutability
    activeMilestone = SPECIAL_VALUES.noMilestoneData as MilestonePickerMilestone
  }

  const anchorRef = useRef<HTMLButtonElement>(null)
  const initialSelectedItems = useMemo(() => {
    return activeMilestone ? [activeMilestone] : []
  }, [activeMilestone])

  const showNoMatchMessage = useMemo<boolean>(() => {
    const hasNoItems = !isServerFetching && !filter && visibleItems.length === 0
    const hasNoFilterResults = !(showNoMatchItem && canCreate) && !!filter && visibleItems.length === 0
    return hasNoItems || hasNoFilterResults
  }, [filter, isServerFetching, visibleItems.length, showNoMatchItem, canCreate])

  const responsiveOnNarrowEnabled = isFeatureEnabled('issues_react_select_panel_fullscreen_on_narrow')
  const createActionItem =
    !isCreateFormOpen && canCreate && showNoMatchItem && filter
      ? {
          id: filter,
          closed: false,
          closedAt: null,
          dueOn: null,
          progressPercentage: 0,
          title: filter,
          url: '',
          __isCreateAction__: true,
        }
      : undefined

  return (
    <ItemPicker
      loading={!isCreateFormOpen && (isLoading || isServerFetching)}
      items={isCreateFormOpen ? [] : visibleItems}
      initialSelectedItems={initialSelectedItems}
      filterItems={filterItems}
      title={isCreateFormOpen ? 'New milestone' : (title ?? LABELS.milestonesHeader)}
      getItemKey={getItemKey}
      convertToItemProps={convertToItemProps}
      isActionItem={isCreateAction}
      placeholderText={`Filter milestones ${
        milestones.length >= VALUES.milestonesMaxPreloadCount
          ? `(${VALUES.milestonesMaxPreloadCount} milestones loaded)`
          : ''
      }`}
      selectionVariant="single"
      onSelectionChange={selected => {
        if (!isCreateFormOpen) onSelectionChange(selected)
      }}
      onClose={() => setIsCreateFormOpen(false)}
      preventClose={isCreateFormOpen && isCreatePending}
      onItemActivation={onItemActivation}
      groups={noMilestoneItem && !includeClosedMilestones ? undefined : groups}
      renderAnchor={props => anchorElement(props, anchorRef)}
      insidePortal={insidePortal}
      height={'large'}
      width={'medium'}
      nested={nested}
      resultListAriaLabel={'Milestone results'}
      triggerOpen={triggerOpen}
      initialFilter={initialFilter}
      selectPanelRef={anchorRef}
      inputRef={filterInputRef}
      keybindingCommandId="item-pickers:open-milestone"
      anchorSide={anchorSide}
      customNoMatchItem={createActionItem}
      improvedNoMatchAccessibility={isCreateFormOpen || showNoMatchMessage}
      noMatchMessage={
        isCreateFormOpen
          ? {
              title: 'New milestone',
              body: '',
              variant: 'empty',
              action: (
                <div ref={focusForm} className={styles.inlineCreateForm}>
                  <IconButton
                    aria-label="Back to milestones"
                    aria-disabled={isCreatePending}
                    icon={ArrowLeftIcon}
                    onClick={closeCreateForm}
                    size="small"
                    variant="invisible"
                    data-component="CreateMilestoneBackButton"
                  />
                  <InlineMilestoneForm
                    repository={{id: repositoryId, nameWithOwner: `${owner}/${repo}`, viewerCanCreateMilestones}}
                    initialTitle={createInitialTitle}
                    onCancel={closeCreateForm}
                    onPendingChange={setIsCreatePending}
                    onSuccess={handleMilestoneCreated}
                  />
                </div>
              ),
            }
          : showNoMatchMessage
            ? {
                title: 'No milestones were found',
                body: 'Try searching with a different query for results.',
                variant: 'empty',
              }
            : undefined
      }
      className={isCreateFormOpen ? styles.inlineCreateList : undefined}
      secondaryAction={
        canCreateInline && !isCreateFormOpen ? (
          <SelectPanel.SecondaryActionButton onClick={() => openCreateForm('')}>
            New milestone
          </SelectPanel.SecondaryActionButton>
        ) : undefined
      }
      responsiveOnNarrow={responsiveOnNarrowEnabled}
    />
  )
}
